// Firebase Storage Service - File upload and management
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';
import type { Attachment } from '@/types';
import { Timestamp } from 'firebase/firestore';

/**
 * Upload a file to Firebase Storage
 * @param file The file to upload
 * @param path The storage path (e.g., 'tickets/[ticketId]/attachments')
 * @returns The download URL of the uploaded file
 */
export const uploadFile = async (file: File, path: string): Promise<string> => {
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const fullPath = `${path}/${timestamp}_${safeName}`;
  const storageRef = ref(storage, fullPath);

  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

/**
 * Upload multiple files
 * @param files Array of files to upload
 * @param path The storage path
 * @returns Array of download URLs
 */
export const uploadFiles = async (files: File[], path: string): Promise<string[]> => {
  const urls = await Promise.all(files.map((file) => uploadFile(file, path)));
  return urls;
};

/**
 * Upload a file and create an Attachment object
 * @param file The file to upload
 * @param path The storage path
 * @returns An Attachment object with metadata
 */
export const uploadAttachment = async (file: File, path: string): Promise<Attachment> => {
  const url = await uploadFile(file, path);
  return {
    id: `att_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    name: file.name,
    url,
    type: file.type,
    size: file.size,
    uploadedAt: Timestamp.now(),
  };
};

/**
 * Upload multiple files as attachments
 * @param files Array of files to upload
 * @param path The storage path
 * @returns Array of Attachment objects
 */
export const uploadAttachments = async (files: File[], path: string): Promise<Attachment[]> => {
  const attachments = await Promise.all(files.map((file) => uploadAttachment(file, path)));
  return attachments;
};

/**
 * Delete a file from Firebase Storage
 * @param url The download URL of the file to delete
 */
export const deleteFile = async (url: string): Promise<void> => {
  const storageRef = ref(storage, url);
  await deleteObject(storageRef);
};

/**
 * Upload a base64 data URL as a file (e.g., for screenshots)
 * @param dataUrl The base64 data URL
 * @param path The storage path
 * @param filename The filename to use
 * @returns The download URL
 */
export const uploadDataUrl = async (
  dataUrl: string,
  path: string,
  filename: string
): Promise<string> => {
  // Convert data URL to blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  const timestamp = Date.now();
  const fullPath = `${path}/${timestamp}_${filename}`;
  const storageRef = ref(storage, fullPath);

  await uploadBytes(storageRef, blob);
  return getDownloadURL(storageRef);
};

// Export all functions as a service object for convenience
export const storageService = {
  uploadFile,
  uploadFiles,
  uploadAttachment,
  uploadAttachments,
  deleteFile,
  uploadDataUrl,
};
